<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import api from '@/api';
import {
  MAX_IP_ADDRESS_RESTRICTION_FORM,
  MAX_IP_ADDRESS_RESTRICTIONS,
} from '@/constants';
import { DgrDialog, DgrIcon } from '@stockmarkteam/donguri-ui';
import { isAxiosError } from 'axios';
import LoadableButton from '@/components/common/atoms/loadable-button.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import IpAddressRestrictionForm from './ip-address-restriction-form.vue';
import { IpAddressRestrictionFormType } from './types';

const emit = defineEmits(['onClose', 'onSubmit']);

const { createSnackbar } = useSnackbar();

const isCreating = ref(false);

const ipAddressRestrictionForms = ref<IpAddressRestrictionFormType[]>([
  {
    formId: 0,
    ipAddress: '',
    description: '',
    isValid: false,
  },
]);

const props = defineProps<{
  currentIpAddressRestrictionsCount: number;
}>();

const totalIpAddressRestrictionsCount = computed(
  () =>
    props.currentIpAddressRestrictionsCount +
    ipAddressRestrictionForms.value.length,
);

// 既に登録されているIpアドレスとフォームの数を合わせた数が上限を超えているかどうか
const isOverIpAddressRestrictionsCount = computed(
  () => totalIpAddressRestrictionsCount.value > MAX_IP_ADDRESS_RESTRICTIONS,
);

// 既に登録されているIpアドレスとフォームの数が上限に達しているかどうか
const isIpAddressRestrictionsAtLimit = computed(
  () => totalIpAddressRestrictionsCount.value === MAX_IP_ADDRESS_RESTRICTIONS,
);

// モーダル内のフォームの数が上限に達しているかどうか(モーダル内でフォームは10件まで表示可能)
const isFormAddable = computed(
  () =>
    ipAddressRestrictionForms.value.length < MAX_IP_ADDRESS_RESTRICTION_FORM,
);

const isCreatable = computed(
  () =>
    ipAddressRestrictionForms.value.every(form => form.isValid) &&
    !isOverIpAddressRestrictionsCount.value,
);

const isAddFormButtonDisabled = computed(
  () => isIpAddressRestrictionsAtLimit.value || !isFormAddable.value,
);

const isCreateModalOpen = ref(false);

watch([isCreateModalOpen], () => {
  if (!isCreateModalOpen.value) {
    ipAddressRestrictionForms.value = [
      {
        formId: 0,
        ipAddress: '',
        description: '',
        isValid: false,
      },
    ];
    emit('onClose');
  }
});

const addForm = () => {
  const lastForm =
    ipAddressRestrictionForms.value[ipAddressRestrictionForms.value.length - 1];
  ipAddressRestrictionForms.value.push({
    formId: lastForm.formId + 1,
    ipAddress: '',
    description: '',
    isValid: false,
  });
};

const deleteForm = (formId: number) => {
  ipAddressRestrictionForms.value = ipAddressRestrictionForms.value.filter(
    form => form.formId !== formId,
  );
};

const createIpAddressRestrictions = async () => {
  isCreating.value = true;
  const ipAddressRestrictions = ipAddressRestrictionForms.value.map(form => ({
    ip_address: form.ipAddress,
    description: form.description,
  }));
  try {
    await api.postIpAddressRestrictionBulkCreate(ipAddressRestrictions);
    createSnackbar({
      message: 'IPアドレスの追加が完了しました',
      type: 'success',
    });
    emit('onSubmit');
  } catch (error) {
    if (
      isAxiosError(error) &&
      error.response?.data.error.includes(
        `Each team can have up to ${MAX_IP_ADDRESS_RESTRICTIONS} IP address restrictions`,
      )
    ) {
      createSnackbar({
        message:
          '保存できませんでした。既に別のメンバーによって処理されています。',
        type: 'error',
      });
    } else {
      createSnackbar({
        message: '保存できませんでした',
        type: 'error',
      });
    }
  }
  isCreating.value = false;
  isCreateModalOpen.value = false;
};
</script>

<template>
  <button
    class="c-btn--small c-btn--auto c-btn--AnewsPrimary c-text-regular--m"
    :class="{ disabled: isIpAddressRestrictionsAtLimit }"
    @click="isCreateModalOpen = true"
    :disabled="isIpAddressRestrictionsAtLimit"
  >
    IPアドレス追加
  </button>

  <DgrDialog
    v-model:open="isCreateModalOpen"
    title="IPアドレスの追加"
    with-footer-divider
  >
    <template #description>
      <div class="modal-form">
        <div class="form-wrapper">
          <div
            v-for="(form, index) in ipAddressRestrictionForms"
            :key="form.formId"
          >
            <IpAddressRestrictionForm
              :is-display-label="index === 0"
              :is-display-delete-button="index !== 0"
              v-model:ip-address="form.ipAddress"
              v-model:description="form.description"
              @on-delete="deleteForm(form.formId)"
              @on-valid="form.isValid = $event"
            />
          </div>
        </div>

        <div>
          <span
            class="add-form-button c-text-regular--s"
            :class="{ disabled: isAddFormButtonDisabled }"
            @click="addForm"
          >
            <DgrIcon name="plus-small" :keep-fill="false" />
            <div class="button-text">追加</div>
          </span>
        </div>

        <div
          v-if="isIpAddressRestrictionsAtLimit"
          class="at-limit-message c-text-regular--s"
        >
          <div>これ以上追加することができません。</div>
          <div>登録可能なIPアドレスの上限数を超えてしまいます。</div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="modal-footer">
        <div class="modal-ip-address-annotation c-text-regular--s">
          <div>
            IPv4の通常の表記及びCIDR表記に対応しています(一括で最大10件登録可能)
          </div>
          <div class="spacing-08"></div>
          <div>通常の表記（例 203.0.113.0）CIDR表記（例 203.0.113.0/24）</div>
        </div>
        <div class="modal-footer-buttons">
          <button
            class="modal-cancel-button c-btn c-btn--auto c-btnOutline"
            @click="isCreateModalOpen = false"
          >
            キャンセル
          </button>

          <LoadableButton
            class="modal-create-button c-btn c-btn--auto c-btn--AnewsPrimary"
            :class="{ disabled: !isCreatable }"
            :disabled="!isCreatable"
            @click="createIpAddressRestrictions"
            :is-loading="isCreating"
          >
            保存
          </LoadableButton>
        </div>
      </div>
    </template>
  </DgrDialog>
</template>

<style lang="scss" scoped>
.modal-form {
  width: 678px;
  height: calc(80vh - 105px); // INFO: remove padding 16px * 2 + footer height
  overflow: scroll;

  .form-wrapper {
    padding-bottom: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .add-form-button {
    display: inline-flex;
    color: $color-green600;
    cursor: pointer;

    .button-text {
      position: relative;
      top: 4px;
    }

    &.disabled {
      color: $color-gray600;
      pointer-events: none;
    }
  }
}

.at-limit-message {
  color: $color-orange1000;
}

.modal-footer {
  width: 100%;
  display: flex;
  justify-content: space-between;

  .modal-ip-address-annotation {
    display: flex;
    flex-flow: column;
    justify-content: flex-start;
    color: $color-gray600;
  }

  .modal-footer-buttons {
    display: flex;
    flex-direction: row;
    gap: 16px;
  }
}
</style>
